/* eslint-disable @typescript-eslint/ban-ts-comment */

import { sizes } from '@lmc/common/constants'
import type { Document, Resident } from '@lmc/ts-common'
import { UserRole } from '@lmc/ts-common'
import { Button, EmptyState, Icon, NoDataIcon, PageHeader } from '@lmc/ui-kit'
import { useAbilityAccess } from 'components/AccessControl'
import useResident from 'hooks/useResident'
import { type FC, useEffect, useState } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import actions from '../../../../actions'
import { showToast } from '../../../../common/utils'
import { DocumentList, Spinner } from '../../../../components/generic'
import ConfirmationDialog from '../../../../LEGACY/ConfirmationDialog'
import Operations from '../../../../operations'
import Selectors from '../../../../selectors'
import { history } from '../../../../store'
import {
  getNoDocumentsMessage,
  getSupportSummary,
  getSupportSummarySeniors,
  groupDocuments,
  MODAL_ID,
  SUPPORT_ARTICLE
} from './utils'

const isDesktop = isBrowser || isTablet

const ResidentDocumentsScreen: FC<{ testId?: string }> = ({ testId }) => {
  const dispatch = useDispatch()

  const params = useParams()

  const id = params?.id

  const { resident, isLoading: isResidentLoading } = useResident({ id })

  const carePlanNominative = useSelector(
    (state: { global: { home: { preferredCarePlansNominative: string } } }) =>
      state.global.home.preferredCarePlansNominative
  )

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: { preferredPeopleNominative: string } } }) =>
      state.global.home.preferredPeopleNominative
  )

  const user = useSelector(
    (state: { global: { user: Resident } }) => state.global.user
  )

  const userRole = useSelector(
    (state: { global: { user: { role: string } } }) => state.global.user?.role
  )

  const documents = useSelector((state) =>
    // @ts-ignore
    Selectors.carePlanDocumentSelector(state, { router: { params } })
  )

  const [isLoading, setIsLoading] = useState(false)

  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null)

  const fetchData = async () => {
    if (!documents || !documents.length) {
      setIsLoading(false)
    }

    if (id) {
      await dispatch(Operations.loadList('documents', { resident: id }, false))
    }

    await dispatch(
      Operations.loadList(
        'care-plans',
        {
          resident: id,
          lite: 1,
          isRevision: false
        },
        false
      )
    )

    await dispatch(
      Operations.loadList(
        'care-plans',
        {
          resident: id,
          lite: 1,
          isRevision: false
        },
        false
      )
    )

    setIsLoading(false)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchData()
  }, [params])

  const hasRolePermission = useAbilityAccess([
    {
      i: 'update',
      this: 'Resident'
    }
  ])

  const canViewDocument = useAbilityAccess([{ i: 'view', this: 'Document' }])

  const openDialog = (id: string) => setDocumentToDelete(id)

  const closeDialog = () => setDocumentToDelete(null)

  const handleDelete = () => {
    dispatch(Operations.deleteDocument(documentToDelete, 'documents'))
    showToast({ type: 'info', title: 'Deleted a Document' })
    closeDialog()
  }

  const openCreateModal = () => {
    dispatch(
      actions.openModal({
        modalId: MODAL_ID,
        data: { listId: 'documents', type: 'create' }
      })
    )
    dispatch(
      actions.addForm({
        formId: MODAL_ID,
        fields: { resident: id } as any
      })
    )
  }

  const openEditModal = (document: Document) => {
    dispatch(
      actions.openModal({
        modalId: MODAL_ID,
        data: { listId: 'documents', type: 'edit' }
      })
    )
    dispatch(
      actions.addForm({
        formId: MODAL_ID,
        initial: document
      })
    )
  }

  const renderBody = ({
    groupedDocs,
    hasDocuments
  }: {
    groupedDocs: unknown[]
    hasDocuments?: boolean
  }) => {
    if (isLoading)
      return (
        <StyledSpinner
          data-testid={
            testId ? `${testId}-documents-loading` : 'documents-loading'
          }
        />
      )

    if (hasDocuments) {
      return (
        <DocumentList
          documents={groupedDocs}
          listId="documents"
          onDelete={openDialog}
          onEdit={openEditModal}
          carePlanNominative={carePlanNominative}
          testId={testId}
        />
      )
    }

    const emptyStateDescription = !canViewDocument
      ? getSupportSummarySeniors(carePlanNominative)
      : getSupportSummary(carePlanNominative)

    return (
      // @ts-ignore
      <EmptyState
        title={getNoDocumentsMessage(preferredPeopleNominative)}
        icon={<NoDataIcon />}
        description={emptyStateDescription}
        data-testid={
          testId ? `${testId}-documents-empty-state` : 'documents-empty-state'
        }
      />
    )
  }

  if (!documents || !params || !params.id || !user) {
    return <StyledSpinner />
  }

  const { hasDocuments, groupedDocs } = groupDocuments(
    documents,
    carePlanNominative
  )

  const pageHeaderTitle =
    isResidentLoading || !resident
      ? 'Documents'
      : `${resident?.name?.first} ${resident?.name?.last}'s Documents`

  return (
    <div
      data-testid={testId ? `${testId}-documents-screen` : 'documents-screen'}
    >
      <PageHeader
        title={pageHeaderTitle}
        onBack={!isDesktop ? () => history.back?.() : undefined}
        moreInfoLink={
          userRole !== UserRole.seniorCarer ? SUPPORT_ARTICLE : undefined
        }
        sticky
        data-testid={testId ? `${testId}-documents-header` : 'documents-header'}
      >
        <div
          data-testid={
            testId ? `${testId}-documents-actions` : 'documents-actions'
          }
        >
          {hasRolePermission && (
            <Button
              onClick={openCreateModal}
              variant="primary"
              icon={<Icon name="PlusOutlined" />}
              data-testid={
                testId
                  ? `${testId}-documents-add-button`
                  : 'documents-add-button'
              }
            >
              Add a Document
            </Button>
          )}
        </div>
      </PageHeader>
      <Container
        data-testid={
          testId ? `${testId}-documents-content` : 'documents-content'
        }
      >
        <DocumentListContainer
          data-testid={
            testId
              ? `${testId}-documents-list-container`
              : 'documents-list-container'
          }
        >
          {renderBody({ groupedDocs, hasDocuments } as unknown as {
            groupedDocs: unknown[]
            hasDocuments?: boolean | undefined
          })}
        </DocumentListContainer>
        <ConfirmationDialog
          confirmationType="warning"
          isOpen={!!documentToDelete}
          onCancel={closeDialog}
          onConfirmation={handleDelete}
          data-testid={
            testId
              ? `${testId}-documents-delete-dialog`
              : 'documents-delete-dialog'
          }
        >
          <div
            data-testid={
              testId
                ? `${testId}-documents-delete-content`
                : 'documents-delete-content'
            }
          >
            Are you sure you want to delete this document? If you go ahead, it
            can&apos;t be undone. Once it&apos;s gone, it&apos;s gone for good!
          </div>
        </ConfirmationDialog>
      </Container>
    </div>
  )
}

const Container = styled.div`
  padding: 24px 0px 50px;
  margin: 0 auto;
  max-width: ${sizes.childScreen}px;
  display: flex;
  flex-direction: column;
`

const DocumentListContainer = styled.div`
  padding-top: 24px;
`

const StyledSpinner = styled(Spinner)`
  min-height: 194px;
`

export default ResidentDocumentsScreen
